import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { generatePath } from 'react-router';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Dot } from '@sorare/core/src/atoms/icons/Dot';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LinkOverlay } from '@sorare/core/src/atoms/navigation/Box';
import { HeadlineS } from '@sorare/core/src/atoms/typography';
import { MY_SORARE_PURCHASES_INSTANT_BUYS_ID } from '@sorare/core/src/constants/__generated__/routes';
import { Since } from '@sorare/core/src/contexts/intl/Since';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';

import { ItemImgContainer } from '#src/components/ItemPreview/ui.tsx';
import { ItemRoot } from '#src/components/mySorare/common/ui.ts';
import { FlexCard } from '#src/components/token/FlexCard/index.tsx';

import type { MySorarePrimaryOffer_primaryOffer } from './__generated__/index.graphql.ts';

const Wrapper = styled(ItemRoot)`
  align-items: flex-start;
  border: 1px solid var(--c-nd-300);
  border-radius: var(--half-unit);
  display: flex;
  flex-direction: column;
  --padding: var(--double-unit);
  padding: var(--padding);
  /* for linkbox */
  position: relative;
  isolation: isolate;
`;

const HorizontalOverflow = styled(Horizontal)`
  overflow-x: auto;
  width: calc(100% + 2 * var(--padding));
  padding: 0 var(--padding);
  margin: 0 calc(-1 * var(--padding));
`;

type Props = {
  primaryOffer: MySorarePrimaryOffer_primaryOffer;
};

export const PrimaryOffer = ({ primaryOffer }: Props) => {
  return (
    <Wrapper>
      <Horizontal style={{ width: '100%' }}>
        <HeadlineS>
          {primaryOffer.anyCards.length > 1 ? (
            <FormattedMessage
              defaultMessage="{nb, plural, one {# Card} other {# Cards}}"
              values={{ nb: primaryOffer.anyCards.length }}
            />
          ) : (
            <span>{primaryOffer.anyCards[0].anyPlayer.displayName}</span>
          )}
        </HeadlineS>

        {!!primaryOffer.acceptedAt && (
          <>
            <Dot size={0.5} color="var(--c-nd-500)" />{' '}
            <Since date={primaryOffer.acceptedAt} />
          </>
        )}
        <LinkOverlay
          as={Button}
          size="compact"
          color="quaternary"
          to={generatePath(MY_SORARE_PURCHASES_INSTANT_BUYS_ID, {
            id: idFromObject(primaryOffer.id),
          })}
          style={{ marginLeft: 'auto' }}
        >
          <FormattedMessage defaultMessage="See my order" />
        </LinkOverlay>
      </Horizontal>
      <HorizontalOverflow>
        {primaryOffer.anyCards.map(card => (
          <ItemImgContainer key={card.slug}>
            <FlexCard card={card} withLink />
          </ItemImgContainer>
        ))}
      </HorizontalOverflow>
    </Wrapper>
  );
};

PrimaryOffer.fragments = {
  primaryOffer: gql`
    fragment MySorarePrimaryOffer_primaryOffer on TokenPrimaryOffer {
      id
      acceptedAt
      anyCards {
        slug
        anyPlayer {
          slug
          displayName
        }
        ...FlexCard_anyCard
      }
    }
    ${FlexCard.fragments.anyCard}
  ` as TypedDocumentNode<MySorarePrimaryOffer_primaryOffer>,
};
